export class SettingsWindow {
  constructor(devtools) {
    this.devtools = devtools;
  }

  createSettingsWindow() {
    const windowId = 'settings';
    
    if (this.devtools.windowManager.windows.has(windowId)) {
      this.devtools.windowManager.bringToFront(windowId);
      return;
    }

    const windowElement = this.createSettingsWindowElement();
    document.body.appendChild(windowElement);
    
    this.devtools.windowManager.windows.set(windowId, {
      element: windowElement,
      data: null,
      type: 'settings'
    });

    this.devtools.windowManager.makeWindowDraggable(windowElement);
    this.devtools.windowManager.positionWindow(windowElement);
  }

  createSettingsWindowElement() {
    const window = document.createElement('div');
    window.className = 'devtools-window';
    window.style.zIndex = ++this.devtools.windowManager.zIndexCounter;
    window.style.width = '800px';
    window.style.maxWidth = '90vw';
    window.style.height = '600px';
    
    window.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">⚙️ DEVTOOLS SETTINGS</div>
        <div class="devtools-controls">
          <button class="devtools-btn close-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content" style="max-height: 550px; padding: 0;">
        <div class="settings-tabs">
          <button class="settings-tab active" data-tab="themes">🎨 Themes</button>
          <button class="settings-tab" data-tab="layouts">📐 Layouts</button>
          <button class="settings-tab" data-tab="services">🔗 Services</button>
          <button class="settings-tab" data-tab="general">⚙️ General</button>
        </div>
        
        <div class="settings-content">
          <div id="themes-tab" class="tab-content active">
            ${this.createThemesTab()}
          </div>
          
          <div id="layouts-tab" class="tab-content">
            ${this.createLayoutsTab()}
          </div>
          
          <div id="services-tab" class="tab-content">
            ${this.createServicesTab()}
          </div>
          
          <div id="general-tab" class="tab-content">
            ${this.createGeneralTab()}
          </div>
        </div>
      </div>
    `;

    this.setupSettingsEventListeners(window);
    return window;
  }

  createThemesTab() {
    const themes = this.devtools.themeManager.getAvailableThemes();
    const currentTheme = this.devtools.themeManager.currentTheme;
    
    return `
      <div class="settings-section">
        <h3>Theme Selection</h3>
        <div class="theme-grid">
          ${themes.map(theme => `
            <div class="theme-card ${theme.id === currentTheme ? 'active' : ''}" data-theme="${theme.id}">
              <div class="theme-preview" style="
                background: ${theme.colors.background};
                border: 2px solid ${theme.colors.border};
                color: ${theme.colors.text};
              ">
                <div class="preview-header" style="background: ${theme.colors.surface}; color: ${theme.colors.secondary};">
                  ${theme.name}
                </div>
                <div class="preview-content">
                  <span style="color: ${theme.colors.error};">Error</span>
                  <span style="color: ${theme.colors.warning};">Warning</span>
                  <span style="color: ${theme.colors.success};">Success</span>
                </div>
              </div>
              <div class="theme-name">${theme.name}</div>
              ${theme.custom ? '<div class="theme-badge">Custom</div>' : ''}
            </div>
          `).join('')}
        </div>
        
        <div class="theme-actions">
          <button class="devtools-btn create-theme-btn">Create Custom Theme</button>
          <button class="devtools-btn import-theme-btn">Import Theme</button>
          <button class="devtools-btn export-theme-btn">Export Current</button>
        </div>
      </div>
    `;
  }

  createLayoutsTab() {
    const layouts = this.devtools.themeManager.getAvailableLayouts();
    const currentLayout = this.devtools.themeManager.currentLayout;
    
    return `
      <div class="settings-section">
        <h3>Layout Selection</h3>
        <div class="layout-grid">
          ${layouts.map(layout => `
            <div class="layout-card ${layout.id === currentLayout ? 'active' : ''}" data-layout="${layout.id}">
              <div class="layout-preview">
                ${this.getLayoutPreviewSVG(layout)}
              </div>
              <div class="layout-name">${layout.name}</div>
              <div class="layout-description">${this.getLayoutDescription(layout)}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  createServicesTab() {
    const templates = this.devtools.serviceManager.getServiceTemplates();
    const services = this.devtools.serviceManager.services;
    
    return `
      <div class="settings-section">
        <h3>External Services</h3>
        
        <div class="service-status">
          <h4>Connected Services</h4>
          <div id="connected-services">
            ${Array.from(services.entries()).map(([name, service]) => `
              <div class="service-item ${service.status}">
                <span class="service-name">${name}</span>
                <span class="service-status-badge ${service.status}">${service.status}</span>
                <button class="devtools-btn-small" onclick="this.disconnectService('${name}')">Disconnect</button>
              </div>
            `).join('') || '<p>No services connected</p>'}
          </div>
        </div>
        
        <div class="service-templates">
          <h4>Available Services</h4>
          <div class="service-grid">
            ${Object.entries(templates).map(([id, template]) => `
              <div class="service-card" data-service="${id}">
                <div class="service-header">
                  <h5>${template.name}</h5>
                </div>
                <div class="service-description">${template.description}</div>
                <button class="devtools-btn connect-service-btn" data-service="${id}">
                  ${services.has(id) ? 'Reconfigure' : 'Connect'}
                </button>
              </div>
            `).join('')}
          </div>
        </div>
        
        <div class="webhook-section">
          <h4>Webhooks</h4>
          <div class="webhook-form">
            <input type="text" id="webhook-name" placeholder="Webhook name">
            <input type="url" id="webhook-url" placeholder="Webhook URL">
            <button class="devtools-btn" onclick="this.addWebhook()">Add Webhook</button>
          </div>
        </div>
      </div>
    `;
  }

  createGeneralTab() {
    return `
      <div class="settings-section">
        <h3>General Settings</h3>
        
        <div class="setting-group">
          <label>
            <input type="checkbox" id="auto-capture" checked> 
            Auto-capture errors
          </label>
        </div>
        
        <div class="setting-group">
          <label>
            <input type="checkbox" id="real-time-updates" checked> 
            Real-time updates
          </label>
        </div>
        
        <div class="setting-group">
          <label>
            <input type="checkbox" id="pixelated-text" checked> 
            Pixelated text rendering
          </label>
        </div>
        
        <div class="setting-group">
          <label>Max error history:</label>
          <input type="number" id="max-errors" value="100" min="10" max="1000">
        </div>
        
        <div class="setting-group">
          <label>Window opacity:</label>
          <input type="range" id="window-opacity" min="0.5" max="1" step="0.1" value="0.9">
        </div>
        
        <div class="setting-actions">
          <button class="devtools-btn reset-settings-btn">Reset to Defaults</button>
          <button class="devtools-btn export-settings-btn">Export Settings</button>
          <button class="devtools-btn import-settings-btn">Import Settings</button>
        </div>
      </div>
    `;
  }

  getLayoutPreviewSVG(layout) {
    const svgs = {
      floating: `
        <svg width="60" height="40" viewBox="0 0 60 40">
          <rect x="5" y="5" width="20" height="15" fill="currentColor" opacity="0.7"/>
          <rect x="15" y="10" width="20" height="15" fill="currentColor" opacity="0.8"/>
          <rect x="25" y="15" width="20" height="15" fill="currentColor" opacity="0.9"/>
        </svg>
      `,
      sidebar: `
        <svg width="60" height="40" viewBox="0 0 60 40">
          <rect x="0" y="0" width="40" height="40" fill="currentColor" opacity="0.3"/>
          <rect x="40" y="0" width="20" height="40" fill="currentColor" opacity="0.8"/>
        </svg>
      `,
      panel: `
        <svg width="60" height="40" viewBox="0 0 60 40">
          <rect x="0" y="0" width="60" height="25" fill="currentColor" opacity="0.3"/>
          <rect x="0" y="25" width="60" height="15" fill="currentColor" opacity="0.8"/>
        </svg>
      `,
      grid: `
        <svg width="60" height="40" viewBox="0 0 60 40">
          <rect x="0" y="0" width="28" height="18" fill="currentColor" opacity="0.8"/>
          <rect x="32" y="0" width="28" height="18" fill="currentColor" opacity="0.8"/>
          <rect x="0" y="22" width="28" height="18" fill="currentColor" opacity="0.8"/>
          <rect x="32" y="22" width="28" height="18" fill="currentColor" opacity="0.8"/>
        </svg>
      `
    };
    
    return svgs[layout.id] || svgs.floating;
  }

  getLayoutDescription(layout) {
    const descriptions = {
      floating: 'Independent draggable windows',
      sidebar: 'Docked to the right side',
      panel: 'Bottom panel with tabs',
      grid: 'Organized in a grid layout'
    };
    
    return descriptions[layout.id] || 'Custom layout';
  }

  setupSettingsEventListeners(windowElement) {
    // Close button
    const closeBtn = windowElement.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => {
      this.devtools.windowManager.closeWindow('settings');
    });

    // Tab switching
    const tabs = windowElement.querySelectorAll('.settings-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.dataset.tab;
        this.switchTab(windowElement, tabId);
      });
    });

    // Theme selection
    const themeCards = windowElement.querySelectorAll('.theme-card');
    themeCards.forEach(card => {
      card.addEventListener('click', () => {
        const themeId = card.dataset.theme;
        this.devtools.themeManager.applyTheme(themeId);
        this.updateActiveTheme(windowElement, themeId);
      });
    });

    // Layout selection
    const layoutCards = windowElement.querySelectorAll('.layout-card');
    layoutCards.forEach(card => {
      card.addEventListener('click', () => {
        const layoutId = card.dataset.layout;
        this.devtools.themeManager.applyLayout(layoutId);
        this.updateActiveLayout(windowElement, layoutId);
      });
    });

    // Service connection
    const connectBtns = windowElement.querySelectorAll('.connect-service-btn');
    connectBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const serviceId = btn.dataset.service;
        this.showServiceConnectionDialog(serviceId);
      });
    });

    // Theme action buttons
    const createThemeBtn = windowElement.querySelector('.create-theme-btn');
    if (createThemeBtn) {
      createThemeBtn.addEventListener('click', () => this.createCustomTheme());
    }

    const importThemeBtn = windowElement.querySelector('.import-theme-btn');
    if (importThemeBtn) {
      importThemeBtn.addEventListener('click', () => this.importTheme());
    }

    const exportThemeBtn = windowElement.querySelector('.export-theme-btn');
    if (exportThemeBtn) {
      exportThemeBtn.addEventListener('click', () => this.exportCurrentTheme());
    }

    // Settings action buttons
    const resetBtn = windowElement.querySelector('.reset-settings-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetSettings());
    }

    const exportSettingsBtn = windowElement.querySelector('.export-settings-btn');
    if (exportSettingsBtn) {
      exportSettingsBtn.addEventListener('click', () => this.exportSettings());
    }

    const importSettingsBtn = windowElement.querySelector('.import-settings-btn');
    if (importSettingsBtn) {
      importSettingsBtn.addEventListener('click', () => this.importSettings());
    }
  }

  switchTab(windowElement, tabId) {
    // Update tab buttons
    windowElement.querySelectorAll('.settings-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabId);
    });

    // Update tab content
    windowElement.querySelectorAll('.tab-content').forEach(content => {
      content.classList.toggle('active', content.id === `${tabId}-tab`);
    });
  }

  updateActiveTheme(windowElement, themeId) {
    windowElement.querySelectorAll('.theme-card').forEach(card => {
      card.classList.toggle('active', card.dataset.theme === themeId);
    });
  }

  updateActiveLayout(windowElement, layoutId) {
    windowElement.querySelectorAll('.layout-card').forEach(card => {
      card.classList.toggle('active', card.dataset.layout === layoutId);
    });
  }

  showServiceConnectionDialog(serviceId) {
    const templates = this.devtools.serviceManager.getServiceTemplates();
    const template = templates[serviceId];
    
    if (!template) return;

    // Create connection dialog
    const dialog = document.createElement('div');
    dialog.className = 'devtools-window service-dialog';
    dialog.style.zIndex = ++this.devtools.windowManager.zIndexCounter;
    dialog.style.width = '400px';
    
    dialog.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">Connect to ${template.name}</div>
        <div class="devtools-controls">
          <button class="devtools-btn close-dialog-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content">
        <p>${template.description}</p>
        <form class="service-form">
          ${template.fields.map(field => `
            <div class="form-group">
              <label for="${field.name}">${field.label}${field.required ? ' *' : ''}:</label>
              <input 
                type="${field.type}" 
                id="${field.name}" 
                name="${field.name}"
                ${field.required ? 'required' : ''}
                placeholder="${field.label}"
              >
            </div>
          `).join('')}
          
          <div class="form-actions">
            <button type="button" class="devtools-btn cancel-btn">Cancel</button>
            <button type="submit" class="devtools-btn connect-btn">Connect</button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(dialog);
    this.devtools.windowManager.positionWindow(dialog);

    // Setup dialog event listeners
    dialog.querySelector('.close-dialog-btn').addEventListener('click', () => {
      dialog.remove();
    });

    dialog.querySelector('.cancel-btn').addEventListener('click', () => {
      dialog.remove();
    });

    dialog.querySelector('.service-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const formData = new FormData(e.target);
      const credentials = Object.fromEntries(formData.entries());
      
      // Register and connect service
      this.devtools.serviceManager.registerService(serviceId, {
        ...template,
        ...credentials,
        enabled: true
      });
      
      const result = await this.devtools.serviceManager.connectService(serviceId, credentials);
      
      if (result.success) {
        alert(`Successfully connected to ${template.name}!`);
        dialog.remove();
        // Refresh settings window if open
        this.refreshServicesTab();
      } else {
        alert(`Connection failed: ${result.message}`);
      }
    });
  }

  refreshServicesTab() {
    const settingsWindow = this.devtools.windowManager.windows.get('settings');
    if (settingsWindow) {
      const servicesTab = settingsWindow.element.querySelector('#services-tab');
      servicesTab.innerHTML = this.createServicesTab();
    }
  }
  // Theme management methods

  createCustomTheme() {
    const name = prompt('Enter custom theme name:');
    if (!name) return;

    try {
      const themeId = this.devtools.themeManager.createCustomTheme(name);
      this.devtools.themeManager.applyTheme(themeId);
      
      // Refresh themes tab
      this.refreshThemesTab();
      
      alert(`Custom theme "${name}" created and applied!`);
    } catch (error) {
      alert(`Failed to create theme: ${error.message}`);
    }
  }

  importTheme() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const themeId = this.devtools.themeManager.importTheme(e.target.result);
          this.refreshThemesTab();
          alert('Theme imported successfully!');
        } catch (error) {
          alert(`Failed to import theme: ${error.message}`);
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  }

  exportCurrentTheme() {
    const currentTheme = this.devtools.themeManager.currentTheme;
    const themeData = this.devtools.themeManager.exportTheme(currentTheme);
    
    if (!themeData) {
      alert('No theme to export');
      return;
    }

    const blob = new Blob([themeData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `devtools-theme-${currentTheme}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
  }

  // Settings management methods
  resetSettings() {
    if (confirm('Reset all settings to defaults? This cannot be undone.')) {
      localStorage.removeItem('devtools-theme');
      localStorage.removeItem('devtools-layout');
      
      this.devtools.themeManager.applyTheme('default');
      this.devtools.themeManager.applyLayout('floating');
      
      alert('Settings reset to defaults');
      this.refreshAllTabs();
    }
  }

  exportSettings() {
    const settings = {
      theme: this.devtools.themeManager.currentTheme,
      layout: this.devtools.themeManager.currentLayout,
      customThemes: Array.from(this.devtools.themeManager.themes.values()).filter(t => t.custom),
      services: Array.from(this.devtools.serviceManager.services.entries()),
      timestamp: Date.now()
    };

    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `devtools-settings-${Date.now()}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
  }

  importSettings() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const settings = JSON.parse(e.target.result);
          
          // Import custom themes
          if (settings.customThemes) {
            settings.customThemes.forEach(theme => {
              this.devtools.themeManager.themes.set(theme.id, theme);
            });
          }
          
          // Apply theme and layout
          if (settings.theme) {
            this.devtools.themeManager.applyTheme(settings.theme);
          }
          
          if (settings.layout) {
            this.devtools.themeManager.applyLayout(settings.layout);
          }
          
          this.refreshAllTabs();
          alert('Settings imported successfully!');
        } catch (error) {
          alert(`Failed to import settings: ${error.message}`);
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  }

  // Refresh methods
  refreshThemesTab() {
    const settingsWindow = this.devtools.windowManager.windows.get('settings');
    if (settingsWindow) {
      const themesTab = settingsWindow.element.querySelector('#themes-tab');
      themesTab.innerHTML = this.createThemesTab();
      this.setupThemeEventListeners(settingsWindow.element);
    }
  }

  refreshAllTabs() {
    const settingsWindow = this.devtools.windowManager.windows.get('settings');
    if (settingsWindow) {
      const themesTab = settingsWindow.element.querySelector('#themes-tab');
      const layoutsTab = settingsWindow.element.querySelector('#layouts-tab');
      const servicesTab = settingsWindow.element.querySelector('#services-tab');
      
      themesTab.innerHTML = this.createThemesTab();
      layoutsTab.innerHTML = this.createLayoutsTab();
      servicesTab.innerHTML = this.createServicesTab();
      
      this.setupThemeEventListeners(settingsWindow.element);
      this.setupLayoutEventListeners(settingsWindow.element);
      this.setupServiceEventListeners(settingsWindow.element);
    }
  }

  setupThemeEventListeners(windowElement) {
    const themeCards = windowElement.querySelectorAll('.theme-card');
    themeCards.forEach(card => {
      card.addEventListener('click', () => {
        const themeId = card.dataset.theme;
        this.devtools.themeManager.applyTheme(themeId);
        this.updateActiveTheme(windowElement, themeId);
      });
    });
  }

  setupLayoutEventListeners(windowElement) {
    const layoutCards = windowElement.querySelectorAll('.layout-card');
    layoutCards.forEach(card => {
      card.addEventListener('click', () => {
        const layoutId = card.dataset.layout;
        this.devtools.themeManager.applyLayout(layoutId);
        this.updateActiveLayout(windowElement, layoutId);
      });
    });
  }

  setupServiceEventListeners(windowElement) {
    const connectBtns = windowElement.querySelectorAll('.connect-service-btn');
    connectBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const serviceId = btn.dataset.service;
        this.showServiceConnectionDialog(serviceId);
      });
    });
  }
}